import React, { FC, forwardRef, MouseEventHandler, ReactNode, useImperativeHandle, useState } from "react";
import {
    Box,
    Button,
    Menu,
    MenuItem,
    PopoverOrigin,
    SxProps,
    Tooltip,
    TooltipProps,
} from "@mui/material";
import { useTheme,Theme } from '@mui/material/styles';

interface IwMenuItemPropsInterface {
    label: string;
    id: string | number;
    prefix?: ReactNode;
    suffix?: ReactNode;
    tooltipProps?: any;
    onClick?: MouseEventHandler;
    disabled?: boolean;
}

interface IwMenuPropsInterface {
    items: IwMenuItemPropsInterface[];
    label: ReactNode;
    anchorOrigin?: PopoverOrigin;
    transformOrigin?: PopoverOrigin;
    tooltipProps?: any;
    triggerComponentSx?: SxProps;
    disableDefaultWrapper?: boolean;
}

const prefixSuffixContainerStyles =(theme: Theme)=> ( {
    color: theme?.custom?.mainIconColor,
    svg: {
        display: "block",
    },
});

const IwMenu: FC<IwMenuPropsInterface> = forwardRef((props,ref) => {
    const {
        items,
        label,
        anchorOrigin,
        transformOrigin,
        tooltipProps,
        triggerComponentSx,
        disableDefaultWrapper=false
    } = props;
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>(null);
    const isOpen = !!anchorEl;
    const theme = useTheme();
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleMenuOpen = (anchor: HTMLButtonElement) => {
        setAnchorEl(anchor);
    };

    const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        handleMenuOpen(event.currentTarget);
    };
    useImperativeHandle(ref, () => ({
        closeMenu: handleMenuClose,
    }));

    const triggerButton = !disableDefaultWrapper ? (
        <Button
            sx={{
                padding: 0,
                minWidth: 0,
                color: theme?.custom?.mainIconColor,
                ":hover": {
                    color: theme?.custom?.mainButtonHighlight,
                },
                ...triggerComponentSx,
            }}
            onClick={handleButtonClick}
        >
            {label}
        </Button>
    ): (
        React.cloneElement(label as React.ReactElement, {
            onClick: handleButtonClick,
        })
    );

    return (
        <>
            {!!tooltipProps?.title ? (
                <Tooltip {...tooltipProps}>{triggerButton}</Tooltip>
            ) : (
                triggerButton
            )}
            <Menu
                open={isOpen}
                anchorEl={anchorEl}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                onClose={handleMenuClose}
            >
                {items?.map((item) => {
                    const { label, id, prefix, suffix, onClick,disabled } = item;

                    return (
                        <MenuItem
                            key={id}
                            sx={{
                                fontWeight: 400,
                                padding: "8px 16px",
                                ":hover": {
                                    backgroundColor: theme?.custom?.hoverOptionColor,
                                }
                            }}
                            onClick={(event) => {
                                onClick(event);
                                handleMenuClose();
                            }}
                            disabled={disabled}
                        >
                            {prefix && (
                                <Box
                                    sx={{
                                        ...prefixSuffixContainerStyles,
                                        marginRight: "8px",
                                    }}
                                >
                                    {prefix}
                                </Box>
                            )}
                            <Box sx={{ flexGrow: 1 }}>{label}</Box>
                            {suffix && (
                                <Box
                                    sx={{
                                        ...prefixSuffixContainerStyles,
                                        marginLeft: "16px",
                                    }}
                                >
                                    {suffix}
                                </Box>
                            )}
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
});

IwMenu.defaultProps = {
    anchorOrigin: {
        vertical: "bottom",
        horizontal: "right",
    },
    transformOrigin: {
        vertical: "top",
        horizontal: "right",
    },
    tooltipProps: {
        title: null,
        children: null,
    },
    triggerComponentSx: {},
};

export default IwMenu;
